import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';

import * as moment from 'moment-timezone';


export enum UtcToLocalTimeFormat {
  FULL = 'full',
  FULL_TIME = 'full_time',
  SHORT = 'short',
  CUSTOM = 'custom',
  GLOBAL_DATE = 'global_date',
  WITH_TIMEZONE = 'with_timezone'
}

@Injectable({
  providedIn: 'root',
})
export class UtcConverterService {
  constructor(private datePipe: DatePipe) {}

  public convertUtcToLocalTime(
    gmtDate: string,
    format: UtcToLocalTimeFormat = UtcToLocalTimeFormat.FULL
  ): string {
    if (!this.isValidDate(gmtDate)) {
      return gmtDate;
    }

    // ✅ Fix: Trim microseconds to milliseconds
    gmtDate = gmtDate.replace(/(\.\d{3})\d+/, '$1');

    // ✅ Fix: Ensure UTC indicator if missing
    if (!gmtDate.endsWith('Z')) {
      gmtDate += 'Z';
    }

    const newDate = new Date(gmtDate);

    if (!this.isValidDate(newDate)) {
      return gmtDate;
    }

    // ✅ Format based on enum
    switch (format) {
      case UtcToLocalTimeFormat.SHORT:
        return this.datePipe.transform(newDate, 'dd/MM/yyyy');
      case UtcToLocalTimeFormat.FULL:
        return this.datePipe.transform(newDate, 'dd/MM/yyyy HH:mm');
      case UtcToLocalTimeFormat.FULL_TIME:
      case UtcToLocalTimeFormat.CUSTOM:
        return this.datePipe.transform(newDate, "dd/MM/yyyy 'at' hh:mm a");
      case UtcToLocalTimeFormat.WITH_TIMEZONE:
        const abbr = this.getTimezoneAbbreviation();
        return `${this.datePipe.transform(newDate, "dd/MM/yyyy HH:mm")} ${abbr}`;
      case UtcToLocalTimeFormat.GLOBAL_DATE:
        return this.datePipe.transform(newDate, "yyyy-MM-dd'T'HH:mm:ss.SSS");
      default:
        return newDate.toDateString();
    }
  }

  public convertLocalTimeToUtc(localDate: string): string {
    const date = new Date(localDate);
    return date.toUTCString();
  }

  private isValidDate(date: any): boolean {
    return date && new Date(date).toString() !== 'Invalid Date';
  }


  private TIMEZONE_ABBR_MAP: Record<string, string> | null = null;

  private CUSTOM_ABBR_MAP: Record<string, string> = {
    'Asia/Kuwait': 'AST',
    'Asia/Riyadh': 'AST',
    'Asia/Singapore': 'SGT',
    'Asia/Kolkata': 'IST',
  };

  getTimezoneAbbreviation(): string {
    if (!this.TIMEZONE_ABBR_MAP) {
      this.TIMEZONE_ABBR_MAP = {};
      moment.tz.names().forEach(tz => {
        const abbr = moment.tz(tz).zoneAbbr();
        this.TIMEZONE_ABBR_MAP![tz] = abbr;
      });
    }

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (this.CUSTOM_ABBR_MAP[userTimeZone]) {
      return this.CUSTOM_ABBR_MAP[userTimeZone];
    }

    const abbr = this.TIMEZONE_ABBR_MAP[userTimeZone];

    if (abbr && abbr !== 'GMT') {
      return abbr;
    } else {
      const offset = new Date().toLocaleTimeString('en-us', { timeZoneName: 'short', timeZone: userTimeZone });
      const match = offset.match(/GMT[+-]\d{1,2}/);
      return match ? match[0] : 'GMT';
    }
  }

}
